import React from 'react';
import TileGroup from '../TileGroup';
import Day from './Day';
import { getDayOfWeek } from '../shared/dates';
import {
	setDate,
	startOfDay,
	startOfMonth,
	endOfMonth,
	getMonth,
	isSameDay,
	getDate as getDayOfMonth,
	parse,
} from 'date-fns';
import { isAvailable, isRestricted, isSelected } from '../shared/utils';
import { formatCalendarNightlyRate } from '../shared/formatCalendarRate';
import { cn } from '@fshr/shared-react';
import { Oj } from '@fshr/shared/types';

type DaysProps = {
	activeStartDate: Date;
	showFixedNumberOfWeeks?: boolean;
	showNeighboringMonth?: boolean;
	unavailableDates: Date[];
	restrictedDates: Date[];
	baseClassName?: string;
	isMobile?: boolean;
	availabilityData?: Oj.CalendarAvailability[];
	nightlyRatesExperiment?: boolean;
} & Omit<
	React.ComponentProps<typeof TileGroup>,
	'dateTransform' | 'dateType' | 'end' | 'renderTile' | 'start'
> &
	Omit<React.ComponentProps<typeof Day>, 'classes' | 'currentMonthIndex' | 'date' | 'point'>;

export default function Days(props: DaysProps) {
	const {
		activeStartDate,
		showFixedNumberOfWeeks,
		showNeighboringMonth,
		value,
		unavailableDates,
		restrictedDates,
		baseClassName,
		isMobile = false,
		availabilityData,
		nightlyRatesExperiment,
		locale = 'en-US',
		...otherProps
	} = props;
	// Helper function to get nightly rate for a specific date
	const getNightlyRateForDate = (date: Date): string | undefined => {
		if (!availabilityData) return undefined;

		const monthStart = startOfMonth(date);
		const monthKey = `${monthStart.getFullYear()}-${String(monthStart.getMonth() + 1).padStart(2, '0')}-01`;

		const availabilityEntry = availabilityData.find(a => a.date === monthKey);

		if (!availabilityEntry?.nightlyRates || !availabilityEntry.nightlyRates.length) {
			return undefined;
		}

		const dayOfMonth = getDayOfMonth(date);
		const index = dayOfMonth - 1;

		if (index < 0 || index >= availabilityEntry.nightlyRates.length) {
			return undefined;
		}

		const rate = availabilityEntry.nightlyRates[index];
		const status = availabilityEntry.status?.[index];
		if (rate == null || rate === 0 || ['.', 'R', '0'].includes(status)) return undefined;

		return formatCalendarNightlyRate(rate);
	};

	const hasFixedNumberOfWeeks = showFixedNumberOfWeeks || showNeighboringMonth;
	const dayOfWeek = getDayOfWeek(startOfDay(startOfMonth(activeStartDate)));
	const offset = hasFixedNumberOfWeeks ? 0 : dayOfWeek;
	// Flag used in Tile.tsx component to show a full circle when the value is a single date
	const isSameDaySelection =
		isMobile &&
		((value instanceof Date && !Number.isNaN(value.getTime())) ||
			(Array.isArray(value) &&
				value[0] instanceof Date &&
				value[1] instanceof Date &&
				isSameDay(value[0], value[1])));

	/**
	 * Defines on which day of the month the grid shall start. If we simply show current
	 * month, we obviously start on day one, but if showNeighboringMonth is set to
	 * true, we need to find the beginning of the week the first day of the month is in.
	 */
	const start = (hasFixedNumberOfWeeks ? -dayOfWeek : 0) + 1;

	/**
	 * Defines on which day of the month the grid shall end. If we simply show current
	 * month, we need to stop on the last day of the month, but if showNeighboringMonth
	 * is set to true, we need to find the end of the week the last day of the month is in.
	 */
	const end = (() => {
		if (showFixedNumberOfWeeks) {
			// Always show 6 weeks
			return start + 6 * 7 - 1;
		}

		const monthLastDate = startOfDay(endOfMonth(activeStartDate));
		const daysInMonth = monthLastDate.getDate();

		if (showNeighboringMonth) {
			const daysUntilEndOfTheWeek = 7 - getDayOfWeek(monthLastDate) - 1;
			return daysInMonth + daysUntilEndOfTheWeek;
		}

		return daysInMonth;
	})();
	return (
		<TileGroup
			className={cn(`${baseClassName}__month-view__days`, {
				'spacing-stack-2xs': !isMobile,
				'spacing-stack-l': isMobile,
			})}
			count={7}
			dateTransform={day => {
				return startOfDay(setDate(activeStartDate, day));
			}}
			end={end}
			nightlyRatesExperiment={nightlyRatesExperiment}
			renderTile={({ date, ...otherTileProps }) => {
				const formattedPrice = getNightlyRateForDate(date);
				return (
					<Day
						key={Math.floor(date.getTime() / 1000)}
						{...otherProps}
						{...otherTileProps}
						isAvailable={isAvailable(date, unavailableDates)}
						isRestricted={isRestricted(date, restrictedDates)}
						isSelected={isSelected(date, { value: value })}
						isSameDaySelection={isSameDaySelection}
						nightlyRatesExperiment={nightlyRatesExperiment}
						isMobile={isMobile}
						activeStartDate={activeStartDate}
						currentMonthIndex={getMonth(activeStartDate)}
						formattedPrice={formattedPrice}
						date={date}
						locale={locale}
						baseClassName={baseClassName}
					/>
				);
			}}
			offset={offset}
			start={start}
			value={value}
		/>
	);
}
